import React, { useCallback, useMemo } from "react";
import { ResponsiveLine, Point } from "@nivo/line";
import { useIntl } from "react-intl";
import { Select } from "./select";
import { BookingFilterCriteria, ChartDataDashboard, FilterDashboard } from "../types/types";
import { getCurrencyCodeSymbol } from "../../loyaltyHistorical/helper";
import { isMoneyFilterCriteria } from "../hooks/useGetDashboard";
import { maxBy } from "lodash";
import { chartMessages } from "../messages";

const fontSizeAxis = "14px";

export function formatNumberWithLetters(number) {
  switch (true) {
    case number >= 1000 && number < 1000000:
      return (number / 1000).toFixed(2) + "K";
    case number >= 1000000 && number < 1000000000:
      return (number / 1000000).toFixed(2) + "M";
    case number >= 1000000000:
      return (number / 1000000000).toFixed(2) + "B";
    default:
      return number.toString();
  }
}

const theme = {
  axis: {
    ticks: {
      text: { fill: "background: rgba(134, 143, 142, 1);", fontSize: fontSizeAxis },
    },
  },
};

interface LineChartProps {
  current: ChartDataDashboard[];
  past: ChartDataDashboard[];
  dataFirstFilter: FilterDashboard[];
  valueFirstFilter: FilterDashboard;
  handleChangeFirstFilter: (selected: FilterDashboard) => void;
  dataSecondFilter: FilterDashboard[];
  valueSecondFilter: FilterDashboard;
  handleChangeSecondFilter: (selected: FilterDashboard) => void;
  defaultCurrency: string;
}

function textWidth(str) {
  let el = document.createElement("span");
  document.body.appendChild(el);

  el.style.fontSize = fontSizeAxis;
  el.style.fontWeight = "600";
  el.style.height = "auto";
  el.style.width = "auto";
  el.style.position = "absolute";
  el.style.whiteSpace = "no-wrap";
  el.innerHTML = str;

  const width = Math.ceil(el.clientWidth);
  document.body.removeChild(el);
  return width;
}

export const LineChart = ({
  current,
  past,
  dataFirstFilter,
  valueFirstFilter,
  handleChangeFirstFilter,
  dataSecondFilter,
  valueSecondFilter,
  handleChangeSecondFilter,
  defaultCurrency,
}: LineChartProps) => {
  const intl = useIntl();

  const data = [
    {
      id: intl.formatMessage(chartMessages.currentPeriod),
      data: current,
    },
    {
      id: intl.formatMessage(chartMessages.lastYear),
      data: past,
    },
  ];

  const showCurrency = useMemo(
    () =>
      isMoneyFilterCriteria({ criteria: valueFirstFilter })
        ? getCurrencyCodeSymbol(defaultCurrency)
        : "",
    [defaultCurrency, valueFirstFilter]
  );

  const marginLeftByMaxValue = useMemo(() => {
    const LEFT_AXIS_TICK_PADDING = 12;
    const MARGIN_LEFT_MAX = 200;
    const emptyValue = {
      x: 0,
      y: 0,
      formattedY: "",
    };
    const maxInChart = maxBy([...current, ...past], "formattedY.length") || emptyValue;
    if (maxInChart.formattedY) {
      const longestValueWidth = textWidth(maxInChart.formattedY);
      return Math.min(Math.ceil(longestValueWidth + LEFT_AXIS_TICK_PADDING), MARGIN_LEFT_MAX);
    }
    return LEFT_AXIS_TICK_PADDING;
  }, [current, past]);

  const backgroundColor = useCallback((point: Point) => {
    return point.serieId === intl.formatMessage(chartMessages.currentPeriod) ? "#636FC1" : "#EAEBED";
  }, [intl]);

  const textColor = useCallback((point: Point) => {
    return point.serieId === intl.formatMessage(chartMessages.currentPeriod) ? "white" : "#979CB1";
  }, [intl]);

  const getFullDate = useCallback(
    point => {
      const currentLine = data.find(d => d.id === point.serieId);
      if (currentLine?.data) {
        const date = currentLine.data.find(
          item => item.x === point.data.x && item.y === point.data.y
        );
        return date?.fullDate;
      }
      return "";
    },
    [data]
  );

  const filterIsBooking = useMemo(
    () =>
      valueFirstFilter.value === BookingFilterCriteria.BY_BOOKING_DATE ||
      valueFirstFilter.value === BookingFilterCriteria.BY_CHECKIN_DATE,
    [valueFirstFilter]
  );

  return (
    <div className="border rounded h-full p-[20px]">
      <div className="flex flex-row w-full min-[1855px]:mb-5 max-[1855px]:mb-8 max-[1445px]:mb-2">
        <div className="w-1/5">
          <span className="font-semibold text-lg">{intl.formatMessage(chartMessages.chartTitle)}</span>
        </div>
        <div className="w-4/5 flex flex-row gap-3">
          <div className="w-[50%] position-relative">
            <Select
              value={valueFirstFilter.labelName}
              options={dataFirstFilter}
              handleChange={handleChangeFirstFilter}
              showAsterik={true}
            />
            <span className="text-[10px] text-[#979CB1] position-absolute top-[40px] left-[0px] min-[1855px]:w-[750px] max-[1855px]:w-[540px] max-[1533px]:w-[380px]">
              *{" "}
              {filterIsBooking
                ? intl.formatMessage(chartMessages.annotationBooking)
                : intl.formatMessage(chartMessages.annotationCheckout)}
            </span>
          </div>
          <div className="w-[30%]">
            <Select
              value={valueSecondFilter.labelName}
              labelValue={valueSecondFilter.labelValue}
              options={dataSecondFilter}
              handleChange={handleChangeSecondFilter}
            />
          </div>
          {current?.length > 0 && past?.length > 0 && (
            <div className="w-[20%] flex flex-col justify-center">
              <div className="flex flex-row items-center">
                <div className=" h-2.5 w-2.5 bg-[#636FC1] mr-1" />
                <span className="text-xs text-[#979CB1]">
                  {intl.formatMessage(chartMessages.currentPeriod)}
                </span>
              </div>
              <div className="flex flex-row items-center">
                <div className=" h-2.5 w-2.5 bg-[#979CB1] mr-1" />
                <span className="text-xs text-[#979CB1]">
                  {intl.formatMessage(chartMessages.lastYear)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div style={{ height: "85%", width: "100%" }}>
        <ResponsiveLine
          theme={theme}
          colors={["#636FC1", "#979CB1"]}
          isInteractive
          animate
          data={data}
          margin={{
            top: 10,
            right: 30,
            bottom: 45,
            left: marginLeftByMaxValue,
          }}
          curve="monotoneX"
          enableGridX={false}
          lineWidth={4}
          enableSlices="x"
          pointSize={0}
          axisBottom={{
            tickRotation: 45,
            tickSize: 10,
            tickValues: 5,
            format: values => {
              return <tspan className="font-weight-bold fill-[#979CB1]"> {values} </tspan>;
            },
          }}
          yScale={{
            type: "linear",
            max: "auto",
            min: 0,
            stacked: false,
            reverse: false,
          }}
          axisLeft={{
            tickValues: 7,
            format: values => {
              if (isMoneyFilterCriteria({ criteria: valueFirstFilter })) {
                const formattedNumber = formatNumberWithLetters(values);
                return (
                  <tspan className="font-weight-bold fill-[#979CB1] pl-2">{formattedNumber}</tspan>
                );
              }
              return (
                <tspan textAnchor="end" className="font-weight-bold fill-[#979CB1]  pl-2">
                  {Math.floor(values) === values ? values : ""}
                </tspan>
              );
            },
          }}
          sliceTooltip={({ slice }) => (
            <div>
              {slice.points.map((point) => (
                <div
                  key={point.id}
                  style={{
                    backgroundColor: backgroundColor(point),
                    borderRadius: "4px",
                    padding: "5px 10px",
                    color: textColor(point),
                    minWidth: "100px",
                    lineHeight: "13.8px",
                    display: "flex",
                    flexDirection: "column",
                    marginBottom: "10px",
                    height: "37px"
                  }}>
                  <p className="text-[12px]">
                    <span className="font-semibold mb-[5px]">{point.data.y.toString()} {showCurrency}</span>
                    <br/>
                    {getFullDate(point)}
                  </p>
                </div>
              ))}
            </div>
          )}
        />
      </div>
    </div>
  );
};
